export interface BookingQueryParams {
	roomGuid?: string;
	dealGuid?: string;
	packageGuid?: string;
	accommodationTypes?: string;
	promotionCode?: string;
	languageCode?: string;
	startDate?: string;
	endDate?: string;
	occupancy?: string;
}

export class BookingRedirectHelper {
	private static readonly qParamMapping: Record<
		keyof BookingQueryParams,
		string
	> = {
		roomGuid: 'roomGUID',
		dealGuid: 'dealGUID',
		packageGuid: 'packageGUID',
		accommodationTypes: 'accommodationTypes',
		promotionCode: 'promotionCode',
		languageCode: 'languageCode',
		startDate: 'startDate',
		endDate: 'endDate',
		occupancy: 'occupancy',
	};

	/**
	 * Generates the correct booking tool redirect url based on the provided parameters.
	 * @param bookingToolUrl The base URL of the booking tool.
	 * @param hotelSlug The slug of the hotel.
	 * @param queryParams Optional query parameters for the booking tool.
	 *
	 * @returns The complete redirect URL for the booking tool.
	 */
	static getRedirectUrl(
		bookingToolUrl: string,
		hotelSlug: string,
		queryParams?: BookingQueryParams,
	): string {
		let redirectUrl = bookingToolUrl;

		if (hotelSlug) {
			redirectUrl += '?hotelSlug=' + hotelSlug;
		} else {
			// Redirect will not work without a hotel slug
			return bookingToolUrl;
		}

		if (queryParams) {
			redirectUrl += this.getRedirectQueryParams(queryParams);
		}

		return redirectUrl;
	}

	static getRedirectQueryParams(queryParams: BookingQueryParams) {
		let redirectUrl = '';

		for (const [key, urlParam] of Object.entries(this.qParamMapping)) {
			const value = queryParams[key as keyof BookingQueryParams];
			if (value) {
				redirectUrl += `&${urlParam}=${value}`;
			}
		}

		return redirectUrl;
	}

	/**
	 * Returns the booking query params as a record keyed by the URL parameter
	 * names — suitable for binding to Angular's `[queryParams]`. Only the keys
	 * present in `queryParams` are written, so callers can omit a field
	 * (e.g. `promotionCode`) instead of clearing it from the existing URL
	 * under `queryParamsHandling: 'merge'`.
	 */
	static toQueryParamRecord(
		queryParams: BookingQueryParams,
	): Record<string, string | undefined> {
		const record: Record<string, string | undefined> = {};
		for (const [key, urlParam] of Object.entries(this.qParamMapping)) {
			if (key in queryParams) {
				record[urlParam] = queryParams[key as keyof BookingQueryParams];
			}
		}
		return record;
	}
}
